import { SortOrderEnum } from '@/services/noteGoApi/noteGoApiSchemas';
import { Filter } from '@/types/filters';
import {
  VIEW_MODE_STORAGE_KEY,
  LIST_VIEW_PREFS_STORAGE_KEY,
  type ViewMode,
  type ListViewPreferences,
  type DateRange,
} from '@/types/viewMode';
import { create } from 'zustand';
import type { NoteSortField } from '@/constants/noteListColumns';

const getStoredViewMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'list' || stored === 'card') return stored;
    // Migrate legacy values from previous 3-mode implementation (compact-list | expanded-list | card)
    if (stored === 'compact-list' || stored === 'expanded-list') return 'list';
  } catch {
    // localStorage unavailable
  }
  return 'list';
};

const getStoredListViewPreferences = (): ListViewPreferences => {
  try {
    const stored = localStorage.getItem(LIST_VIEW_PREFS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ListViewPreferences;
      return {
        showThumbnails: typeof parsed.showThumbnails === 'boolean' ? parsed.showThumbnails : true,
        showTags: typeof parsed.showTags === 'boolean' ? parsed.showTags : false,
      };
    }
  } catch {
    // localStorage unavailable or invalid JSON
  }
  return { showThumbnails: true, showTags: false };
};

interface SearchFilterNotesState {
  searchQuery: string;
  selectedTags: string[];
  selectedFilters: Filter[];
  currentPagePointer: string | null;
  notesPerPage: number;
  sortBy: NoteSortField;
  sortOrder: SortOrderEnum;
  viewMode: ViewMode;
  listViewPreferences: ListViewPreferences;
  dateRange: DateRange;

  setSearchQuery: (query: string) => void;
  setSelectedTags: (tags: string[]) => void;
  setSelectedFilters: (filters: Filter[]) => void;
  setCurrentPagePointer: (pointer: string | null) => void;
  setNotesPerPage: (count: number) => void;
  setSortBy: (sortBy: NoteSortField) => void;
  setSortOrder: (order: SortOrderEnum) => void;
  setViewMode: (mode: ViewMode) => void;
  setListViewPreferences: (prefs: Partial<ListViewPreferences>) => void;
  setDateRange: (range: DateRange) => void;
  resetFilters: () => void;
}

export const useSearchFilterNotesStore = create<SearchFilterNotesState>((set, get) => ({
  searchQuery: '',
  selectedTags: [],
  selectedFilters: [],
  currentPagePointer: null,
  notesPerPage: 10,
  sortBy: 'created_at',
  sortOrder: 'desc',
  viewMode: getStoredViewMode(),
  listViewPreferences: getStoredListViewPreferences(),
  dateRange: { start: null, end: null },

  setSearchQuery: (query) => set({ searchQuery: query }),
  setSelectedTags: (tags) => set({ selectedTags: tags }),
  setSelectedFilters: (filters) => set({ selectedFilters: filters }),
  setCurrentPagePointer: (pointer) => set({ currentPagePointer: pointer }),
  setNotesPerPage: (count) => set({ notesPerPage: count }),
  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (order) => set({ sortOrder: order }),

  setViewMode: (mode) => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage unavailable
    }
    set({ viewMode: mode });
  },

  setListViewPreferences: (prefs) => {
    const current = get().listViewPreferences;
    const updated = { ...current, ...prefs };
    try {
      localStorage.setItem(LIST_VIEW_PREFS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // localStorage unavailable
    }
    set({ listViewPreferences: updated });
  },

  setDateRange: (range) => set({ dateRange: range }),

  resetFilters: () =>
    set({
      searchQuery: '',
      selectedTags: [],
      selectedFilters: [],
      currentPagePointer: null,
      notesPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      dateRange: { start: null, end: null },
      // Note: viewMode and listViewPreferences are intentionally NOT reset
      // They are user preferences, not filter state
    }),
}));
